import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "What distinguishes your approach to property sourcing?",
    answer: "We combine nearly three decades of family expertise with deep relationships across our markets. Rather than relying solely on public listings, we cultivate long-standing connections with estate owners, developers, and local advisors who share opportunities before they reach the open market. This approach ensures our clients encounter properties that align with both their investment objectives and lifestyle aspirations.",
  },
  {
    question: "How do you structure your advisory fees?",
    answer: "As fiduciaries, we operate with complete transparency. Our advisory fees are structured as a percentage of the transaction value, typically ranging from 1-3% depending on complexity and market. This aligns our interests directly with yours — we succeed when you secure exceptional value. We never accept undisclosed commissions from sellers, and all fee arrangements are documented clearly before engagement.",
  },
  {
    question: "What considerations apply to cross-border acquisitions?",
    answer: "International property acquisition involves multiple jurisdictions, each with distinct legal frameworks, tax implications, and ownership structures. We coordinate with trusted attorneys and tax advisors in each market to structure acquisitions optimally — whether through personal ownership, corporate vehicles, or trust arrangements. Our role is to anticipate complexities and present solutions, not problems.",
  },
  {
    question: "What investment thresholds do you typically work with?",
    answer: "Our focus is on properties of significance — estates that represent meaningful investments and lasting value. Entry points vary by market: Monaco commands premiums above €5 million given its scarcity, while Spain and Italy offer compelling opportunities from €2 million. We welcome conversations about your investment parameters and objectives before making recommendations.",
  },
  {
    question: "How do you approach currency considerations?",
    answer: "Currency timing can materially impact acquisition costs and long-term returns. We partner with specialist foreign exchange advisors who provide preferential rates compared to traditional banking channels, forward contracts to secure favorable positions, and strategic counsel on timing. For substantial transactions, these considerations can represent savings of 1-3% compared to standard transfers.",
  },
  {
    question: "What does your due diligence process encompass?",
    answer: "Thorough due diligence protects your investment and provides confidence in your acquisition. Our process includes comprehensive title verification, planning and zoning review, structural and environmental assessments, rental potential analysis where relevant, and neighbourhood trajectory evaluation. We verify seller credentials, outstanding obligations, and any encumbrances. Our standard is complete transparency before commitment.",
  },
  {
    question: "Do you provide ongoing support after acquisition?",
    answer: "Our relationship extends well beyond the transaction. We connect clients with vetted property management partners, coordinate refurbishment and interior design projects, facilitate rental arrangements for investment properties, and provide ongoing market intelligence. Many clients appreciate having a single trusted point of contact for all matters relating to their international property holdings.",
  },
  {
    question: "What tax considerations should inform my decision?",
    answer: "Tax implications vary significantly across jurisdictions — from Monaco's favourable position to the purchase taxes and annual levies in Spain and Italy. Each market offers different structures, exemptions, and optimisation opportunities depending on residency status, holding period, and intended use. We work alongside international tax specialists to ensure your structure is both compliant and efficient.",
  },
  {
    question: "What timelines should I anticipate?",
    answer: "Transaction timelines reflect local legal requirements and market practices. Dubai transactions can complete within weeks given its streamlined processes. Monaco typically requires two to three months. Spain and Italy generally proceed over three to six months, allowing for comprehensive due diligence and notarial requirements. We establish realistic expectations from the outset and provide regular progress updates throughout.",
  },
  {
    question: "Can you facilitate financing for international purchases?",
    answer: "While we do not provide financing directly, we maintain relationships with private banks and international lenders experienced in cross-border property finance. Leverage availability varies by market and client profile, typically ranging from 50-70% loan-to-value. We facilitate introductions to appropriate institutions and support the structuring process to complement your overall financial planning.",
  },
];

const FAQSection = () => {
  return (
    <section id="faq" className="py-24 lg:py-32 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
            Expert Counsel
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
            Questions That
            <span className="block italic font-medium">Matter Most</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Thoughtful answers for discerning investors navigating 
            international property investment.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card border border-border/50 rounded-xl px-6 data-[state=open]:border-primary/30 transition-colors"
              >
                <AccordionTrigger className="font-display text-lg text-foreground hover:text-primary py-6 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-body text-muted-foreground leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-muted-foreground mb-4">
            Have a specific question not covered here?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Contact us for a confidential discussion
            <span className="text-lg">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;